import { useMemo } from "react";

import { useQueries } from "@tanstack/react-query";

import { useRequest } from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import type { Asset } from "~/v2/api/rest";
import {
  type GetOptionSnapshotsResponse,
  getOptionChainSnapshotsWithMeta,
  getOptionsContracts,
  type OptionContract,
  type OptionFeed,
} from "~/v2/api/rest/data";
import { OPTION_CHAIN_SNAPSHOTS_POLLING_INTERVAL } from "~/v2/constants";

export interface OptionContractWithSnapshot extends OptionContract {
  snapshot?: GetOptionSnapshotsResponse["snapshots"][string];
}

export interface UseGetOptionsDataArgs {
  asset: Asset | undefined;
  /** The expirations to load, in display order. */
  expirationDates: readonly string[];
  isCrypto: boolean;
}

export interface UseGetOptionsDataResult {
  optionContracts: OptionContractWithSnapshot[];
  isContractsFetchedAfterMount: boolean;
  isContractsRefetching: boolean;
  /**
   * Feed that served the snapshots, from the `X-Feed` response header. The only
   * signal that reflects both the OPRA agreement and the plan's real-time flag,
   * which is why the request never passes an explicit `feed`.
   */
  feed?: OptionFeed;
  /** When the newest snapshot payload arrived, used to age a frozen chain. */
  snapshotsReceivedAt?: number;
  isLoading: boolean;
  hasError: boolean;
  /** True when a background refresh failed but earlier rows are still on screen. */
  isRefreshError: boolean;
  /** 422 from the contracts endpoint: the symbol has no listed options. */
  isUnsupportedSymbol: boolean;
  refetch: () => void;
}

const isUnsupportedSymbolError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  const response = (error as { response?: { status?: number } }).response;
  return response?.status === 422;
};

export const useGetOptionsData = ({
  asset,
  expirationDates,
  isCrypto,
}: UseGetOptionsDataArgs): UseGetOptionsDataResult => {
  const symbol = asset?.symbol;
  const isEnabled = !isCrypto && !!asset && expirationDates.length > 0;

  // `useQueries`: expiration count is dynamic; `useRequest` has no batch form.
  const contractQueries = useQueries({
    queries: expirationDates.map((date) => ({
      queryKey: queryKeys.options.contracts(symbol, date),
      queryFn: () =>
        getOptionsContracts({
          underlying_symbol: symbol,
          expiration_date: date,
        }),
      enabled: isEnabled,
      // 422 = no listed options; treat as empty state, not a toast.
      meta: { showErrorToast: false },
    })),
  });

  // One ranged poll for the stack — per-expiration polling burned the MD budget.
  // Not the shared v2 snapshots hook: the chain needs response headers it discards.
  const firstExpiration = expirationDates[0];
  const lastExpiration = expirationDates[expirationDates.length - 1];

  const snapshotQuery = useRequest(
    queryKeys.options.chainSnapshotsRange(
      symbol,
      firstExpiration,
      lastExpiration,
    ),
    () =>
      getOptionChainSnapshotsWithMeta({
        underlying_symbol: symbol ?? "",
        expiration_date_gte: firstExpiration,
        expiration_date_lte: lastExpiration,
      }),
    {
      enabled: isEnabled,
      refetchInterval: OPTION_CHAIN_SNAPSHOTS_POLLING_INTERVAL,
      staleTime: OPTION_CHAIN_SNAPSHOTS_POLLING_INTERVAL,
      meta: { showErrorToast: false },
    },
  );

  // `useQueries` returns a new array each render; key the join on payload age.
  const updatedAt = [...contractQueries, snapshotQuery]
    .map((query) => query.dataUpdatedAt)
    .join(",");

  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on `updatedAt`, see above
  const contracts: OptionContractWithSnapshot[] = useMemo(() => {
    const snapshots = snapshotQuery.data?.snapshots;
    return contractQueries.flatMap((query) =>
      (query.data?.option_contracts ?? []).map((contract) => ({
        ...contract,
        snapshot: snapshots?.[contract.symbol],
      })),
    );
  }, [updatedAt]);

  const queries = [...contractQueries, snapshotQuery];
  const contractsError = contractQueries.find((query) => query.error)?.error;
  const hasRows = contracts.length > 0;
  const hasError = queries.some((query) => query.error);

  return {
    optionContracts: contracts,
    isContractsFetchedAfterMount: contractQueries.every(
      (query) => query.isFetchedAfterMount,
    ),
    isContractsRefetching: contractQueries.some((query) => query.isRefetching),
    feed: snapshotQuery.data?.feed,
    snapshotsReceivedAt: snapshotQuery.data?.receivedAt,
    isLoading: isEnabled && queries.some((query) => query.isLoading),
    hasError: hasError && !hasRows,
    isRefreshError: hasError && hasRows,
    isUnsupportedSymbol: isUnsupportedSymbolError(contractsError),
    refetch: () => {
      for (const query of queries) {
        void query.refetch();
      }
    },
  };
};
